import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { ScanLicenseButton } from "./Clients";
import BookingProofs from "../../components/pilotage/BookingProofs";
import BookingCommunications from "../../components/pilotage/BookingCommunications";
import Breadcrumb from "../../components/Breadcrumb";
import { api, formatEUR, formatEURDecimal, formatDateFR, formatApiError, photoUrl } from "../../lib/api";
import { emitDataChanged } from "../../lib/dataBus";
import { fleetLabelMaps, categoryLabel, vehiclePrimaryLabel, vehicleOptionLabel } from "../../lib/vehicleLabel";
import { confirmAction } from "../../components/ConfirmDialog";
import BusinessForm from "../../components/pilotage/BookingEditor";
import { toast } from "sonner";
import {
  Loader2, Mail, Phone, User, Calendar, MapPin, Trash2, Car, Hash,
  FileText, ClipboardCheck, Pencil, Save, X, Upload,
  IdCard, File as FileIcon, Eye, Paperclip, Send, PenLine, CheckCircle2, Link2 as LinkIcon, KeyRound, Tag,
} from "lucide-react";

const STATUS = {
  confirmed: { label: "Confirmée", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  pending: { label: "En attente", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  in_progress: { label: "En cours", color: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  completed: { label: "Terminée", color: "bg-slate-200 text-slate-700", dot: "bg-slate-500" },
};

const DOC_TYPES = [
  { key: "driver_license", label: "Permis de conduire", icon: IdCard },
  { key: "id_card", label: "Carte d'identité", icon: IdCard },
  { key: "passport", label: "Passeport", icon: IdCard },
  { key: "proof_of_address", label: "Justificatif de domicile", icon: FileIcon },
  { key: "other", label: "Autre", icon: Paperclip },
];

export default function VendorBookingDetail({ embedded = false, embeddedId = null }) {
  const params = useParams();
  const id = embeddedId || params.id;
  const navigate = useNavigate();
  const Wrap = embedded ? EmbedWrap : VendorLayout;
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [typeMap, setTypeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null); // 'vehicle' | 'client' | 'dates'

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/vendor/bookings/${id}`),
      api.get("/vendor/vehicles"),
    ])
      .then(([b, v]) => {
        setData(b.data);
        setVehicles(v.data.vehicles || []);
        // Charge documents client
        return api.get(`/vendor/clients/${encodeURIComponent(b.data.booking.client_email)}/documents`);
      })
      .then((r) => setDocuments(r.data.documents || []))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    api.get("/vendor/settings")
      .then(({ data }) => { const m = fleetLabelMaps(data.fleet || data.settings?.fleet || {}); setCatMap(m.catMap); setTypeMap(m.typeMap); })
      .catch(() => {});
    api.get("/vendor/locations").then(({ data }) => setLocations(data.locations || [])).catch(() => {});
  }, []);

  // Ouverture directe en mode édition via ?edit=1 (depuis les cartes de survol) — formulaire métier complet
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (loading || !data) return;
    if (searchParams.get("edit") === "1") {
      setEdit(true);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const patchBooking = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/vendor/bookings/${id}`, patch);
      toast.success("Réservation mise à jour");
      setEdit(null);
      load();
      emitDataChanged("bookings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const STATUS_CONSEQUENCES = {
    confirmed: "La réservation sera marquée comme confirmée : le départ pourra être préparé et les documents envoyés.",
    pending: "La réservation repassera en attente de confirmation.",
    in_progress: "La réservation sera marquée comme location en cours (véhicule considéré comme remis au client).",
    completed: "La location sera clôturée et considérée comme terminée.",
    cancelled: "Le véhicule redeviendra disponible sur la période et les tâches associées seront clôturées. Cette action peut avoir des conséquences sur les documents et paiements.",
  };
  const changeStatus = async (k) => {
    const ref = booking.reference || `#${booking.id.slice(0, 8).toUpperCase()}`;
    const ok = await confirmAction({
      title: k === "cancelled" ? "Annuler cette réservation ?" : `Changer le statut de la réservation ?`,
      message: `Réservation ${ref} — ${booking.client_name}.\n${STATUS_CONSEQUENCES[k] || ""}`,
      confirmLabel: k === "cancelled" ? "Confirmer l'annulation" : "Confirmer le changement",
      cancelLabel: k === "cancelled" ? "Conserver la réservation" : "Annuler",
      variant: k === "cancelled" ? "danger" : "warning",
    });
    if (ok) patchBooking({ status: k });
  };

  const remove = async () => {
    const ref = booking?.reference || `#${id.slice(0, 8).toUpperCase()}`;
    const ok = await confirmAction({
      title: "Supprimer définitivement cette réservation ?",
      message: `La réservation ${ref} et son historique seront définitivement supprimés. Les documents et paiements associés ne seront plus accessibles. Cette action est IRRÉVERSIBLE.`,
      confirmLabel: "Supprimer définitivement",
      variant: "danger",
      requireText: "SUPPRIMER",
    });
    if (!ok) return;
    await api.delete(`/vendor/bookings/${id}`);
    emitDataChanged("bookings");
    navigate("/vendor/pilotage?tab=bookings");
  };

  const reloadDocs = async (email) => {
    const r = await api.get(`/vendor/clients/${encodeURIComponent(email)}/documents`);
    setDocuments(r.data.documents || []);
  };

  if (loading) {
    return (
      <Wrap>
        <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
      </Wrap>
    );
  }
  if (err || !data) {
    return (
      <Wrap>
        <div className="w-full p-10 text-center">
          <h2 className="font-heading text-xl">Réservation introuvable</h2>
          <p className="text-slate-500 mt-1">{err}</p>
          <Link to="/vendor/pilotage?tab=bookings" className="mt-4 inline-flex text-violet-600 hover:underline">Retour</Link>
        </div>
      </Wrap>
    );
  }
  const { booking, vehicle } = data;
  const s = STATUS[booking.status] || STATUS.confirmed;

  // Édition : formulaire métier complet prérempli (identique à la création de réservation)
  if (edit) {
    return (
      <Wrap>
        <div className="h-full overflow-y-auto">
          <BusinessForm
            mode="reservation"
            record={booking}
            vehicles={vehicles}
            locations={locations}
            onBack={() => setEdit(false)}
            onSaved={() => { setEdit(false); load(); emitDataChanged("bookings"); }}
          />
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <Breadcrumb
          items={[
            { label: "Pilotage", href: "/vendor/pilotage" },
            { label: "Réservations", href: "/vendor/pilotage?tab=bookings" },
            { label: "Fiche réservation" },
          ]}
          className="mb-5 flex-wrap"
        />

        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {s.label}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 mt-2 break-words">Fiche réservation <span className="text-slate-400" data-testid="booking-reference">{booking.reference || `#${booking.id.slice(0, 8).toUpperCase()}`}</span></h1>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-2">
              Créée le {formatDateFR(booking.created_at.substring(0, 10))}
              {booking.source && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wide" data-testid="booking-source">{booking.source}</span>}
              {(booking.start_time || booking.end_time) && (
                <span className="text-xs text-slate-400">Départ {booking.start_time || "—"} · Retour {booking.end_time || "—"}</span>
              )}
            </div>
          </div>
          <button onClick={remove} className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50" data-testid="delete-booking">
            <Trash2 className="h-4 w-4" /> Supprimer
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
          <div className="lg:col-span-2 min-w-0 space-y-4 sm:space-y-6">
            {/* ─── Véhicule + Dates + Prix ─── */}
            <Section
              title="Véhicule & Période"
              editing={edit === "vehicle"}
              onEdit={() => setEdit("vehicle")}
              onCancel={() => setEdit(null)}
              testid="section-vehicle"
            >
              {edit === "vehicle" ? (
                <EditVehicleDates
                  booking={booking}
                  vehicles={vehicles}
                  catMap={catMap}
                  saving={saving}
                  onSubmit={patchBooking}
                />
              ) : (
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="h-44 w-full sm:h-24 sm:w-36 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                    {booking.vehicle_photo && <img src={photoUrl(booking.vehicle_photo)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <div className="text-xs text-slate-500">{categoryLabel(vehicle?.category, catMap)}</div>
                      <div className="font-heading text-lg font-medium text-slate-900 break-words" data-testid="booking-vehicle-label">{vehicle ? vehiclePrimaryLabel(vehicle) : booking.vehicle_name}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      {booking.license_plate && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 font-mono text-slate-900">
                          <Hash className="h-3 w-3" /> {booking.license_plate}
                        </span>
                      )}
                      {vehicle?.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {vehicle.city}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 pt-2 border-t border-slate-100">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 text-slate-400" /> {formatDateFR(booking.start_date)} → {formatDateFR(booking.end_date)}</span>
                      <span>{booking.days} jour{booking.days > 1 ? "s" : ""}</span>
                      <span>{formatEURDecimal(booking.price_per_day)}/jour</span>
                    </div>
                  </div>
                </div>
              )}
            </Section>

            {/* ─── Client ─── */}
            <Section
              title="Client"
              editing={edit === "client"}
              onEdit={() => setEdit("client")}
              onCancel={() => setEdit(null)}
              testid="section-client"
            >
              {edit === "client" ? (
                <EditClient
                  booking={booking}
                  saving={saving}
                  onSubmit={patchBooking}
                />
              ) : (
                <div className="space-y-3 text-sm">
                  <Row icon={User} label="Nom" value={booking.client_name} />
                  <Row icon={Mail} label="Email" value={booking.client_email} />
                  <Row icon={Phone} label="Téléphone" value={booking.client_phone || "—"} />
                </div>
              )}
            </Section>

            {/* ─── Documents client ─── */}
            <ClientDocuments
              email={booking.client_email}
              documents={documents}
              onReload={() => reloadDocs(booking.client_email)}
            />

            {/* ─── Statut ─── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="section-status">
              <div className="flex items-center justify-between mb-4">
                <div className="overline">Statut</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => changeStatus(k)}
                    disabled={saving || booking.status === k}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                      booking.status === k
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"
                    } disabled:opacity-60`}
                    data-testid={`set-status-${k}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ─── Contrat ─── */}
            <ContractSection bookingId={booking.id} />

            {/* ─── Documents & Actions ─── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
              <div className="overline mb-4">Documents & Actions</div>
              {["confirmed", "pending"].includes(booking.status) && !booking.keys_handed_at && (
                <Link
                  to={`/vendor/depart/${booking.id}`}
                  className="mb-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                  data-testid="start-departure-link"
                >
                  <KeyRound className="h-5 w-5" />
                  <div>
                    <div className="text-sm font-bold">Démarrer le départ</div>
                    <div className="text-xs text-violet-200">Parcours guidé : vérification, EDL, contrat, remise des clés</div>
                  </div>
                </Link>
              )}
              {booking.status === "in_progress" && !booking.keys_returned_at && (
                <Link
                  to={`/vendor/retour/${booking.id}`}
                  className="mb-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                  data-testid="start-return-link"
                >
                  <KeyRound className="h-5 w-5" />
                  <div>
                    <div className="text-sm font-bold">Démarrer le retour</div>
                    <div className="text-xs text-sky-200">Parcours guidé : contrôle, comparaison, caution, clôture</div>
                  </div>
                </Link>
              )}
              {booking.keys_handed_at && (
                <div className="mb-3 flex items-center gap-2 rounded-xl bg-sky-50 border border-sky-200 px-4 py-3 text-xs font-semibold text-sky-700" data-testid="keys-handed-banner">
                  <KeyRound className="h-4 w-4" /> Clés remises le {formatDateFR(booking.keys_handed_at.substring(0, 10))}{booking.keys_handed_by ? ` par ${booking.keys_handed_by}` : ""} — location en cours
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <InspectionLink bookingId={booking.id} type="departure" label="État des lieux — Départ" />
                <InspectionLink bookingId={booking.id} type="return" label="État des lieux — Retour" />
              </div>
            </div>

            {/* ─── Preuves & photos (EDL signés, galerie) ─── */}
            <BookingProofs bookingId={booking.id} />

            {/* ─── Communications (historique email, lecture seule) ─── */}
            <BookingCommunications bookingId={booking.id} />
          </div>

          {/* ─── Colonne latérale : sticky desktop avec scroll interne, flux normal en mobile/tablette ─── */}
          <div className="lg:col-span-1 min-w-0">
            <div className="lg:sticky lg:top-6 lg:z-[5] lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1 lg:-mr-1" data-testid="booking-side-panel">
            {/* ─── Résumé financier ─── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="overline">Résumé financier</div>
                {booking.custom_pricing && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase" data-testid="booking-custom-pricing-badge" title="Un tarif a été ajusté manuellement sur cette réservation">
                    <Tag className="h-3 w-3" /> Tarif personnalisé
                  </span>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <Line l="Durée" v={`${booking.days} jour${booking.days > 1 ? "s" : ""}`} />
                <Line l="Prix / jour" v={formatEURDecimal(booking.price_per_day)} />
                <Line l="Sous-total" v={formatEURDecimal(booking.subtotal)} />
                {(booking.options || []).map((o) => (
                  <div key={o.key} className="flex items-center justify-between gap-2" data-testid={`booking-detail-option-${o.key}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0 text-slate-600">
                      <span className="truncate">{o.label}</span>
                      {Number(o.amount) === 0 && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide" data-testid={`booking-detail-offert-${o.key}`}>Offert</span>
                      )}
                    </span>
                    <span className="text-slate-900 tabular-nums shrink-0">+ {formatEURDecimal(o.amount)}</span>
                  </div>
                ))}
                {(booking.discount_amount || 0) > 0 && <Line l="Remise" v={`− ${formatEURDecimal(booking.discount_amount)}`} />}
                {(booking.applied_surcharges || []).filter((s) => s.state === "applied").map((s) => (
                  <div key={`${s.surcharge_id}-${s.event}`} className="flex items-center justify-between gap-2" data-testid={`booking-detail-surcharge-${s.event}-${s.surcharge_id}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0 text-slate-600">
                      <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${s.event === "departure" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{s.event === "departure" ? "Départ" : "Retour"}</span>
                      <span className="truncate">{s.name}</span>
                    </span>
                    <span className="text-slate-900 tabular-nums shrink-0">+ {formatEURDecimal(s.amount)}</span>
                  </div>
                ))}
                {(booking.surcharges_total || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm" data-testid="booking-detail-surcharges-total">
                    <span className="text-slate-500">Total des surcoûts</span>
                    <span className="font-semibold text-slate-700 tabular-nums">+ {formatEURDecimal(booking.surcharges_total)}</span>
                  </div>
                )}
                {(() => {
                  const ref = booking.base_total;
                  if (ref == null || !booking.custom_pricing) return null;
                  const gap = Math.round((ref - ((booking.subtotal || 0) + (booking.options_total || 0))) * 100) / 100;
                  if (Math.abs(gap) < 0.01) return null;
                  return (
                    <div className="flex items-center justify-between text-[12px]" data-testid="booking-detail-pricing-gap">
                      <span className="text-slate-500">{gap > 0 ? "Remise vs tarif grille" : "Majoration vs tarif grille"}</span>
                      <span className={`font-semibold tabular-nums ${gap > 0 ? "text-emerald-600" : "text-amber-600"}`}>{gap > 0 ? "− " : "+ "}{formatEURDecimal(Math.abs(gap))}</span>
                    </div>
                  );
                })()}
                <Line l="Total" v={formatEURDecimal(booking.total)} />
                <div className="border-t border-slate-200 pt-3 flex justify-between">
                  <span className="font-semibold">Revenu net</span>
                  <span className="font-heading text-2xl font-medium text-violet-600">{formatEUR(booking.vendor_revenue)}</span>
                </div>
              </div>
              {booking.pricing_note && (
                <div className="mt-4 text-xs text-violet-700 bg-violet-50 rounded-lg px-3 py-2 border border-violet-100" data-testid="booking-detail-pricing-note">
                  <span className="font-semibold">Motif du tarif : </span>{booking.pricing_note}
                </div>
              )}
              {booking.source === "manual" && (
                <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                  Réservation créée manuellement.
                </div>
              )}
            </div>

            {/* ─── Paiement ─── */}
            <PaymentCard booking={booking} onChanged={load} />

            {/* ─── Facture ─── */}
            <InvoiceSection bookingId={booking.id} booking={booking} />

            {/* ─── Caution ─── */}
            <CautionCard booking={booking} onChanged={load} />

            {/* ─── Notes internes ─── */}
            <InternalNotesCard booking={booking} onChanged={load} />
            </div>
          </div>
        </div>
      </div>
    </Wrap>
  );
}

// ─── Paiement (règlements manuels + lien Stripe + journal) ────────────
const PAYMENT_METHOD_LABELS = {
  especes: "Espèces",
  virement: "Virement",
  cb: "Carte bancaire",
  cheque: "Chèque",
  autre: "Autre",
  stripe: "Stripe (en ligne)",
};

function PaymentCard({ booking, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [linkAmount, setLinkAmount] = useState("");
  const [lastLink, setLastLink] = useState(null);
  const [notifyClient, setNotifyClient] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("virement");
  const [payNote, setPayNote] = useState("");
  const [recording, setRecording] = useState(false);
  const [payments, setPayments] = useState(null);
  const paid = booking.amount_paid || 0;
  const balance = Math.max(0, Math.round(((booking.total || 0) - paid) * 100) / 100);

  const loadPayments = () => {
    api.get(`/vendor/bookings/${booking.id}/payments`)
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => setPayments([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPayments(); }, [booking.id, booking.amount_paid]);

  const createLink = async () => {
    const amount = linkAmount !== "" ? parseFloat(linkAmount) : (booking.deposit_amount && paid < booking.deposit_amount ? Math.min(booking.deposit_amount - paid, balance) : balance);
    if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${booking.id}/payment-link`, {
        amount, origin_url: window.location.origin, notify_client: notifyClient,
      });
      setLastLink(data.payment_url);
      await navigator.clipboard.writeText(data.payment_url).catch(() => {});
      toast.success(notifyClient && data.email_status
        ? `Lien copié et envoyé par email à ${booking.client_email}`
        : "Lien de paiement copié dans le presse-papier");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setCreating(false);
  };

  const recordPayment = async () => {
    const amount = payAmount !== "" ? parseFloat(payAmount) : balance;
    if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
    const ok = await confirmAction({
      title: "Enregistrer cet encaissement ?",
      message: `Un règlement de ${amount.toFixed(2)} € (${payMethod === "cash" ? "espèces" : payMethod === "card" ? "carte" : payMethod === "transfer" ? "virement" : payMethod}) sera enregistré pour ${booking.client_name}. Le solde de la réservation sera mis à jour.`,
      confirmLabel: `Confirmer l'encaissement de ${amount.toFixed(2)} €`,
      variant: "default",
    });
    if (!ok) return;
    setRecording(true);
    try {
      await api.post(`/vendor/bookings/${booking.id}/manual-payment`, {
        amount, method: payMethod, note: payNote.trim() || null,
      });
      toast.success("Règlement enregistré");
      setPayAmount(""); setPayNote("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setRecording(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mt-4" data-testid="payment-card">
      <div className="overline mb-4">Paiement</div>
      <div className="space-y-2 text-sm">
        <Line l="Total" v={formatEURDecimal(booking.total)} />
        {booking.deposit_amount != null && <Line l="Acompte attendu" v={formatEURDecimal(booking.deposit_amount)} />}
        <Line l="Payé" v={formatEURDecimal(paid)} />
        <div className="flex justify-between pt-2 border-t border-slate-100">
          <span className="font-semibold">Solde restant dû</span>
          <span className={`font-semibold ${balance > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="balance-due">{formatEURDecimal(balance)}</span>
        </div>
      </div>
      {balance > 0 ? (
        <div className="mt-4 space-y-4">
          {/* Lien de paiement Stripe */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="number" min="0" max={balance} step="0.01"
                value={linkAmount}
                onChange={(e) => setLinkAmount(e.target.value)}
                placeholder={`Montant (max ${balance.toFixed(2)} €)`}
                className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="payment-link-amount"
              />
              <button
                onClick={createLink}
                disabled={creating}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 whitespace-nowrap"
                data-testid="create-payment-link-btn"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />} Lien de paiement
              </button>
            </div>
            {lastLink && (
              <a href={lastLink} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-violet-600 underline truncate" data-testid="payment-link-url">{lastLink}</a>
            )}
            {booking.client_email && (
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer select-none" data-testid="payment-link-notify-label">
                <input
                  type="checkbox"
                  checked={notifyClient}
                  onChange={(e) => setNotifyClient(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  data-testid="payment-link-notify"
                />
                Envoyer le lien par email à {booking.client_email}
              </label>
            )}
          </div>

          {/* Règlement manuel */}
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2" data-testid="manual-payment-form">
            <div className="text-xs font-semibold text-slate-700">Enregistrer un règlement reçu</div>
            <div className="flex gap-2">
              <input
                type="number" min="0" max={balance} step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder={`${balance.toFixed(2)}`}
                className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="manual-payment-amount"
              />
              <select
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                className="flex-1 min-w-0 rounded-xl border border-slate-200 px-2 py-2 text-xs bg-white focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="manual-payment-method"
              >
                {["virement", "especes", "cb", "cheque", "autre"].map((m) => (
                  <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
                ))}
              </select>
            </div>
            <input
              type="text"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="Note (optionnel)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
              data-testid="manual-payment-note"
            />
            <button
              onClick={recordPayment}
              disabled={recording}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-50"
              data-testid="manual-payment-submit"
            >
              {recording ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Enregistrer le règlement
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600" data-testid="fully-paid-badge">
          <CheckCircle2 className="h-4 w-4" /> Réservation intégralement payée
        </div>
      )}

      {/* Journal des règlements */}
      {payments && payments.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="payments-journal">
          <div className="text-xs font-semibold text-slate-700 mb-2">Journal des règlements</div>
          <div className="space-y-1.5">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs" data-testid="payment-journal-entry">
                <div className="min-w-0">
                  <span className="text-slate-700">{PAYMENT_METHOD_LABELS[p.method] || p.method}</span>
                  <span className="text-slate-400"> · {formatDateFR(p.paid_at)}</span>
                  {p.note && <div className="text-[10px] text-slate-400 truncate">{p.note}</div>}
                </div>
                <span className="font-semibold text-slate-900 shrink-0">{formatEURDecimal(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Facture (snapshot immuable FAC-) ─────────────────────────────────
const INVOICE_STATUS_BADGES = {
  issued: { label: "Émise", cls: "bg-blue-100 text-blue-700" },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-700" },
};

function InvoiceSection({ bookingId, booking }) {
  const [invoices, setInvoices] = useState(null);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get(`/vendor/invoices?booking_id=${bookingId}`)
      .then(({ data }) => setInvoices(data.invoices || []))
      .catch(() => setInvoices([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${bookingId}/invoice`);
      toast.success(`Facture ${data.invoice.reference} générée`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setGenerating(false);
  };

  const hasActive = (invoices || []).some((i) => i.status !== "cancelled");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mt-4" data-testid="invoice-section">
      <div className="overline mb-4">Facture</div>
      {invoices === null ? (
        <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-violet-600" /></div>
      ) : (
        <>
          {invoices.length > 0 && (
            <div className="space-y-2 mb-3">
              {invoices.map((inv) => {
                const badge = INVOICE_STATUS_BADGES[inv.status] || INVOICE_STATUS_BADGES.issued;
                return (
                  <button
                    key={inv.id}
                    onClick={() => navigate(`/vendor/pilotage?tab=bookings&booking=${bookingId}&invoice=${inv.id}`)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all text-left"
                    data-testid={`invoice-item-${inv.reference}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-violet-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{inv.reference}</div>
                        <div className="text-[10px] text-slate-500">{formatDateFR(inv.issue_date)} · {formatEURDecimal(inv.total_ttc)}</div>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${badge.cls}`}>{badge.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          {!hasActive && (
            <button
              onClick={generate}
              disabled={generating}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-semibold hover:border-slate-900 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              data-testid="generate-invoice-btn"
            >
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Générer la facture
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Caution (empreinte remboursable via Stripe) ─────────────────────
const CAUTION_STATUS = {
  none: { label: "Non demandée", cls: "bg-slate-100 text-slate-600" },
  link_sent: { label: "Lien envoyé", cls: "bg-blue-100 text-blue-700" },
  held: { label: "Empreinte encaissée", cls: "bg-emerald-100 text-emerald-700" },
  released: { label: "Restituée", cls: "bg-slate-200 text-slate-700" },
  captured: { label: "Retenue", cls: "bg-red-100 text-red-700" },
};

function CautionCard({ booking, onChanged }) {
  const st = booking.caution_status || "none";
  const conf = CAUTION_STATUS[st] || CAUTION_STATUS.none;
  const [amount, setAmount] = useState(booking.caution_amount ?? "");
  const [captureAmt, setCaptureAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const held = booking.caution_paid_amount || booking.caution_amount || 0;

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setBusy(false);
  };

  const createLink = async () => {
    const amt = amount !== "" ? parseFloat(amount) : null;
    setBusy(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${booking.id}/caution-link`, {
        origin_url: window.location.origin,
        ...(amt != null && !Number.isNaN(amt) ? { amount: amt } : {}),
      });
      setLastLink(data.payment_url);
      await navigator.clipboard.writeText(data.payment_url).catch(() => {});
      toast.success("Lien d'empreinte de caution copié dans le presse-papier");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setBusy(false);
  };

  const release = async () => {
    const ok = await confirmAction({
      title: "Restituer la caution ?",
      message: "La caution sera marquée comme restituée au client. Vous ne pourrez plus effectuer de retenue sur cette caution.",
      confirmLabel: "Confirmer la restitution",
      variant: "warning",
    });
    if (!ok) return;
    call(() => api.post(`/vendor/bookings/${booking.id}/caution/release`), "Caution restituée");
  };

  const capture = async () => {
    const amt = parseFloat(captureAmt);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    const ok = await confirmAction({
      title: "Retenir sur la caution ?",
      message: `${amt.toFixed(2)} € seront retenus sur la caution de ${booking.client_name}. Cette opération financière sera enregistrée et visible du client.`,
      confirmLabel: `Confirmer la retenue de ${amt.toFixed(2)} €`,
      variant: "danger",
    });
    if (!ok) return;
    call(() => api.post(`/vendor/bookings/${booking.id}/caution/capture`, { amount: amt }), "Caution retenue");
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mt-4" data-testid="caution-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Caution</div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold ${conf.cls}`} data-testid="caution-status-badge">
          {conf.label}
        </span>
      </div>
      <div className="space-y-2 text-sm">
        {st === "held" && <Line l="Empreinte encaissée" v={formatEURDecimal(held)} />}
        {st === "captured" && (
          <>
            <Line l="Empreinte encaissée" v={formatEURDecimal(held)} />
            <Line l="Montant retenu" v={formatEURDecimal(booking.caution_captured_amount)} />
            <Line l="À rembourser" v={formatEURDecimal(Math.max(0, held - (booking.caution_captured_amount || 0)))} />
          </>
        )}
        {st === "released" && <Line l="Montant restitué" v={formatEURDecimal(held)} />}
      </div>

      {(st === "none" || st === "link_sent") && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              type="number" min="0" step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Montant de la caution (€)"
              className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
              data-testid="caution-amount-input"
            />
            <button
              onClick={createLink}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap"
              data-testid="caution-link-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />} Lien d'empreinte
            </button>
          </div>
          {lastLink && (
            <a href={lastLink} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-violet-600 underline truncate" data-testid="caution-link-url">{lastLink}</a>
          )}
          <p className="text-[10px] text-slate-400 leading-relaxed">
            Empreinte encaissée via Stripe (paiement remboursable). Le remboursement effectif se fait depuis votre tableau de bord Stripe.
          </p>
        </div>
      )}

      {st === "held" && (
        <div className="mt-3 space-y-2">
          <button
            onClick={release}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 disabled:opacity-50"
            data-testid="caution-release-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Restituer la caution
          </button>
          <div className="flex gap-2">
            <input
              type="number" min="0" max={held} step="0.01"
              value={captureAmt}
              onChange={(e) => setCaptureAmt(e.target.value)}
              placeholder={`Retenir (max ${Number(held).toFixed(2)} €)`}
              className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
              data-testid="caution-capture-amount"
            />
            <button
              onClick={capture}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
              data-testid="caution-capture-btn"
            >
              Retenir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notes internes ──────────────────────────────────────────────────
function InternalNotesCard({ booking, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(booking.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/vendor/bookings/${booking.id}`, { internal_notes: notes });
      toast.success("Notes enregistrées");
      setEditing(false);
      onChanged();
    } catch {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mt-4" data-testid="internal-notes-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Notes internes</div>
        {!editing && (
          <button onClick={() => { setNotes(booking.internal_notes || ""); setEditing(true); }} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="edit-notes-btn">
            <Pencil className="h-3 w-3" /> Modifier
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-violet-600 outline-none resize-none" data-testid="notes-textarea" />
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="flex-1 h-8 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">Annuler</button>
            <button onClick={save} disabled={saving} className="flex-1 h-8 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="save-notes-btn">
              {saving ? "…" : "Enregistrer"}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-600 whitespace-pre-wrap" data-testid="notes-display">{booking.internal_notes || <span className="text-slate-400">Aucune note. Visible uniquement par vous.</span>}</p>
      )}
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────
function Section({ title, editing, onEdit, onCancel, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="overline truncate">{title}</div>
        {editing ? (
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 shrink-0" data-testid={`${testid}-cancel`}>
            <X className="h-3.5 w-3.5" /> Annuler
          </button>
        ) : (
          <button onClick={onEdit} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 shrink-0" data-testid={`${testid}-edit`}>
            <Pencil className="h-3.5 w-3.5" /> Modifier
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Edit vehicle + dates ───────────────────────────────────────────
function EditVehicleDates({ booking, vehicles, catMap = {}, saving, onSubmit }) {
  const curVeh = vehicles.find((v) => v.id === booking.vehicle_id);
  const [form, setForm] = useState({
    vehicle_id: booking.vehicle_id,
    category: curVeh?.category || "",
    start_date: booking.start_date,
    end_date: booking.end_date,
    start_time: booking.start_time || "",
    end_time: booking.end_time || "",
    price_per_day: booking.price_per_day,
  });
  const [availableIds, setAvailableIds] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const tok = useRef(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Disponibilité pour la période, en excluant la réservation elle-même (ne se bloque pas).
  useEffect(() => {
    if (!form.start_date || !form.end_date) { setAvailableIds(null); return; }
    const t = ++tok.current;
    setAvailLoading(true);
    api.get("/vendor/availability", {
      params: { start_date: form.start_date, end_date: form.end_date, pickup_location_id: booking.pickup_location_id || undefined, exclude_booking_id: booking.id },
    })
      .then(({ data }) => { if (t === tok.current) setAvailableIds(data.available_ids || []); })
      .catch(() => { if (t === tok.current) setAvailableIds([]); })
      .finally(() => { if (t === tok.current) setAvailLoading(false); });
  }, [form.start_date, form.end_date]);

  const availSet = useMemo(() => new Set(availableIds || []), [availableIds]);
  const availableVehicles = useMemo(
    () => (availableIds == null ? vehicles.filter((v) => v.id === booking.vehicle_id) : vehicles.filter((v) => availSet.has(v.id))),
    [vehicles, availableIds, availSet, booking.vehicle_id],
  );
  const categories = useMemo(() => {
    const map = {};
    for (const v of availableVehicles) {
      if (!v.category) continue;
      if (!map[v.category]) map[v.category] = { value: v.category, label: categoryLabel(v.category, catMap) || v.category, count: 0 };
      map[v.category].count++;
    }
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [availableVehicles, catMap]);
  const vehiclesInCategory = useMemo(() => availableVehicles.filter((v) => v.category === form.category), [availableVehicles, form.category]);

  // Réévaluation des champs dépendants après recalcul de disponibilité.
  useEffect(() => {
    if (availableIds == null) return;
    const cats = new Set(vehicles.filter((v) => availSet.has(v.id)).map((v) => v.category).filter(Boolean));
    setForm((f) => {
      let cat = f.category, veh = f.vehicle_id;
      if (cat && !cats.has(cat)) { cat = ""; veh = ""; }
      if (veh && !availSet.has(veh)) veh = "";
      return cat === f.category && veh === f.vehicle_id ? f : { ...f, category: cat, vehicle_id: veh };
    });
  }, [availableIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      vehicle_id: form.vehicle_id,
      start_date: form.start_date,
      end_date: form.end_date,
      start_time: form.start_time,
      end_time: form.end_time,
      price_per_day: parseFloat(form.price_per_day) || 0,
    });
  };

  const inputCls = "w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="edit-vehicle-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Date de départ"><input type="date" required value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="edit-start-date" /></Field>
        <Field label="Heure de départ"><input type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} data-testid="edit-start-time" /></Field>
        <Field label="Date de retour"><input type="date" required value={form.end_date} onChange={set("end_date")} className={inputCls} data-testid="edit-end-date" /></Field>
        <Field label="Heure de retour"><input type="time" value={form.end_time} onChange={set("end_time")} className={inputCls} data-testid="edit-end-time" /></Field>
      </div>
      <Field label="Catégorie du bien">
        <select value={form.category} onChange={(e) => { const c = e.target.value; setForm((f) => ({ ...f, category: c, vehicle_id: availableVehicles.some((v) => v.id === f.vehicle_id && v.category === c) ? f.vehicle_id : "" })); }} disabled={availLoading || categories.length === 0} className={`${inputCls} bg-white disabled:bg-slate-50 disabled:text-slate-400`} data-testid="edit-category-select">
          <option value="">{availLoading ? "Recherche des disponibilités…" : "Sélectionner"}</option>
          {categories.map((c) => <option key={c.value} value={c.value}>{c.label} ({c.count})</option>)}
        </select>
        {!availLoading && categories.length === 0 && <p className="mt-1 text-xs text-amber-600" data-testid="edit-no-availability">Aucun bien disponible pour cette période. Modifiez les dates.</p>}
      </Field>
      <Field label="Bien à réserver">
        <select value={form.vehicle_id} onChange={set("vehicle_id")} required disabled={!form.category || vehiclesInCategory.length === 0} className={`${inputCls} bg-white disabled:bg-slate-50 disabled:text-slate-400`} data-testid="edit-vehicle-select">
          <option value="">{!form.category ? "Choisissez d'abord une catégorie" : "Sélectionner"}</option>
          {vehiclesInCategory.map((v) => <option key={v.id} value={v.id}>{vehicleOptionLabel(v)}</option>)}
        </select>
      </Field>
      <Field label="Prix / jour (€)">
        <input type="number" min="0" step="0.01" required value={form.price_per_day} onChange={set("price_per_day")} className={inputCls} data-testid="edit-price-per-day" />
      </Field>
      <button type="submit" disabled={saving} className="w-full h-10 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="edit-vehicle-submit">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
      </button>
    </form>
  );
}

// ─── Edit client ────────────────────────────────────────────────────
function EditClient({ booking, saving, onSubmit }) {
  const [form, setForm] = useState({
    client_name: booking.client_name || "",
    client_email: booking.client_email || "",
    client_phone: booking.client_phone || "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="edit-client-form">
      <Field label="Nom">
        <input type="text" required value={form.client_name} onChange={set("client_name")} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none" data-testid="edit-client-name" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Email">
          <input type="email" required value={form.client_email} onChange={set("client_email")} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none" data-testid="edit-client-email" />
        </Field>
        <Field label="Téléphone">
          <input type="tel" value={form.client_phone} onChange={set("client_phone")} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none" data-testid="edit-client-phone" />
        </Field>
      </div>
      <button type="submit" disabled={saving} className="w-full h-10 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="edit-client-submit">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
      </button>
    </form>
  );
}

// ─── Client documents ──────────────────────────────────────────────
function ClientDocuments({ email, documents, onReload }) {
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("driver_license");
  const fileRef = useRef(null);
  const BACK = process.env.REACT_APP_BACKEND_URL;

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(
        `/vendor/clients/${encodeURIComponent(email)}/documents?doc_type=${docType}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Document ajouté");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    const ok = await confirmAction({
      title: "Supprimer ce document ?",
      message: "Le document sera définitivement supprimé du dossier client.",
      confirmLabel: "Supprimer le document",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/vendor/clients/${encodeURIComponent(email)}/documents/${docId}`);
      toast.success("Document supprimé");
      onReload();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const openDoc = async (path) => {
    // Télécharge avec auth puis ouvre en blob
    try {
      const token = sessionStorage.getItem("cm_token");
      const res = await fetch(`${BACK}/api/vendor/client-documents/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  const docLabel = (t) => DOC_TYPES.find((d) => d.key === t)?.label || t;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 overflow-hidden" data-testid="section-client-docs">
      <div className="mb-4">
        <ScanLicenseButton
          onExtract={async (d) => {
            try {
              await api.put(`/vendor/clients/${encodeURIComponent(email)}`, {
                birth_date: d.birth_date || "",
                license_number: d.license_number || "",
                license_expiry: d.expiry_date || "",
              });
              toast.success(`Permis lu — fiche client mise à jour (${[d.first_name, d.last_name].filter(Boolean).join(" ") || "client"})`);
            } catch {
              toast.error("Impossible d'enregistrer les données du permis");
            }
          }}
        />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="overline">Pièces justificatives</div>
        <div className="flex items-center gap-2">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="flex-1 sm:flex-none text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white min-w-0"
            data-testid="doc-type-select"
          >
            {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <button
            onClick={onPick}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 shrink-0"
            data-testid="doc-upload-btn"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Ajouter
          </button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="doc-file-input" />
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">
          Aucun document. Ajoutez le permis, la carte d'identité, etc.
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200" data-testid={`doc-row-${d.id}`}>
              <FileIcon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{d.label}</div>
                <div className="text-xs text-slate-500">{docLabel(d.type)} · {new Date(d.uploaded_at).toLocaleDateString("fr-FR")}</div>
              </div>
              <button onClick={() => openDoc(d.path)} className="h-8 w-8 rounded-full hover:bg-slate-50 flex items-center justify-center text-slate-500" title="Ouvrir" data-testid={`doc-view-${d.id}`}>
                <Eye className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => remove(d.id)} className="h-8 w-8 rounded-full hover:bg-red-50 flex items-center justify-center text-slate-400 hover:text-red-600" title="Supprimer" data-testid={`doc-delete-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Petits composants ──────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-slate-400" />
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-medium text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function Line({ l, v, muted }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{l}</span>
      <span className={muted ? "font-medium text-slate-500" : "font-medium"}>{v}</span>
    </div>
  );
}

function InspectionLink({ bookingId, type, label }) {
  const [creating, setCreating] = React.useState(false);
  const navigate = useNavigate();

  const handleClick = async () => {
    setCreating(true);
    try {
      await api.post(`/inspections?booking_id=${bookingId}&type=${type}`);
      navigate(`/inspection/${bookingId}/${type}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la création de l'état des lieux");
    } finally {
      setCreating(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={creating}
      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all text-left disabled:opacity-50"
      data-testid={`inspection-${type}-link`}
    >
      <ClipboardCheck className="h-5 w-5 text-violet-600" />
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">Créer et signer</div>
      </div>
    </button>
  );
}


// ─── Contract section — mini-hub (statut + 3 actions + édition) ────────
const CONTRACT_STATUS = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  sent: { label: "Envoyé", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  signature_requested: { label: "Signature demandée", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  signed: { label: "Signé", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
};

function ContractSection({ bookingId }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // 'send' | 'sign' | 'edit'
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${bookingId}`);
      setContract(data.contract);
    } catch {
      setContract(null);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);

  const sendContract = async () => {
    setActing("send");
    try {
      const { data } = await api.post(`/contracts/${bookingId}/send`);
      setContract(data.contract);
      toast.success("Contrat envoyé (simulation)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  const requestSignature = async () => {
    setActing("sign");
    try {
      const { data } = await api.post(`/contracts/${bookingId}/request-signature`);
      setContract(data.contract);
      toast.success("Signature demandée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  const saveBody = async (body) => {
    setActing("edit");
    try {
      const { data } = await api.patch(`/contracts/${bookingId}`, { custom_body: body });
      setContract(data.contract);
      toast.success("Contrat mis à jour");
      setEditOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(null);
    }
  };

  const status = contract?.vendor_status || "draft";
  const meta = CONTRACT_STATUS[status];
  const isSigned = status === "signed";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="section-contract">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="overline truncate">Contrat</div>
        <Link to={`/vendor/pilotage?tab=bookings&booking=${bookingId}&contract=1`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="contract-open-link">
          <FileText className="h-3.5 w-3.5" /> Voir la fiche
        </Link>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
      ) : !contract ? (
        <div className="text-sm text-slate-500">Contrat introuvable.</div>
      ) : (
        <>
          {/* Statut + metas */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`} data-testid="contract-status-badge">
              <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
            </span>
            {contract.sent_at && (
              <span className="text-xs text-slate-500">
                Envoyé le {formatDateFR(contract.sent_at.substring(0, 10))}
              </span>
            )}
            {contract.signature_requested_at && (
              <span className="text-xs text-slate-500">
                · Signature demandée le {formatDateFR(contract.signature_requested_at.substring(0, 10))}
              </span>
            )}
          </div>

          {/* Aperçu du texte custom si présent */}
          {contract.custom_body && (
            <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-600 line-clamp-3 whitespace-pre-wrap" data-testid="contract-body-preview">
              {contract.custom_body}
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              onClick={() => setEditOpen(true)}
              disabled={isSigned}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="contract-edit-btn"
            >
              <Pencil className="h-4 w-4" /> Éditer
            </button>
            <button
              onClick={sendContract}
              disabled={isSigned || acting === "send"}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="contract-send-btn"
            >
              {acting === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer
            </button>
            <button
              onClick={requestSignature}
              disabled={isSigned || status === "signature_requested" || acting === "sign"}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="contract-sign-btn"
            >
              {acting === "sign" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Demander signature
            </button>
          </div>
        </>
      )}

      {editOpen && contract && (
        <ContractEditModal
          initialBody={contract.custom_body || ""}
          saving={acting === "edit"}
          onClose={() => setEditOpen(false)}
          onSubmit={saveBody}
        />
      )}
    </div>
  );
}

function ContractEditModal({ initialBody, saving, onClose, onSubmit }) {
  const [body, setBody] = useState(initialBody);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="contract-edit-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-heading text-lg font-medium text-slate-900">Éditer le contrat</div>
            <div className="text-xs text-slate-500">Personnalisez le texte pour ce client. Laissez vide pour utiliser les conditions par défaut.</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={14}
            placeholder="Ex : Clause spécifique pour ce client, conditions particulières, franchise différente…"
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none"
            data-testid="contract-body-textarea"
          />
        </div>
        <div className="px-5 pb-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="contract-edit-cancel">
            Annuler
          </button>
          <button
            onClick={() => onSubmit(body.trim() || null)}
            disabled={saving}
            className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="contract-edit-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Suivi kilométrage & carburant : géré dans les États des lieux d'entrée/sortie ───


function EmbedWrap({ children }) {
  return <>{children}</>;
}
